'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import { styles } from './UiKitNavigationLanding.styles';

export type Link = { label: string; id: string };

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface UiKitNavigationLandingProps {
  links: Link[];
  bg: string;
  color?: string;
  onClick?: NativeElement['onClick'];
}

const HEADER_OFFSET = 100;

export function UiKitNavigationLanding({
  links,
  bg,
  color,
  ...props
}: UiKitNavigationLandingProps) {
  const slots = styles();

  const [activeSection, setActiveSection] = useState<string | null>(null);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);

    utilScrollToElement(section);
  };

  useEffect(() => {
    const sectionElements = links
      .map((link) => {
        const element = document.getElementById(link.id);
        return element ? { link, element } : null;
      })
      .filter(
        (item): item is { link: Link; element: HTMLElement } => item !== null
      );

    if (sectionElements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            setActiveSection(sectionId);
          }
        });
      },
      {
        rootMargin: `-${HEADER_OFFSET}px 0px -50% 0px`,
        threshold: 0
      }
    );

    sectionElements.forEach(({ element }) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [links]);

  return (
    <div
      className={slots.base()}
      style={{
        background: bg
      }}
      {...props}
    >
      <ul className={slots.wrap()}>
        {links.map((link) => (
          <li key={link.id}>
            <button
              onClick={() => scrollToSection(link.id)}
              style={{ color: color }}
              className={slots.anchor({
                activeItem: activeSection === link.id
              })}
            >
              {link.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
